import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import { SonicGameSelectionData } from "../common/classes/SonicGameSelection";

import { getGameSelections } from "../services/api/game-selections/getGameSelections";
import { postUpdateGameSelection } from "../services/api/game-selections/postUpdateGameSelection";
import { postDeleteGameSelection } from "../services/api/game-selections/postDeleteGameSelection";
import { postShuffleGameSelection } from "../services/api/game-selections/postShuffleGameSelection";

const initialData: {
  gameSelections: SonicGameSelectionData[];
  isLoading: boolean;
  isError: boolean;
} = {
  gameSelections: [],
  isLoading: false,
  isError: false,
};

export const useGetGameSelection = () => {
  const gameSelectionData = useQuery(
    ["gameSelections"],
    ({ signal }) => getGameSelections(signal),
    { staleTime: Infinity }
  );

  if (!gameSelectionData.data) {
    return { ...initialData, isLoading: gameSelectionData.isLoading };
  }

  return {
    gameSelections: gameSelectionData.data,
    isLoading: gameSelectionData.isLoading,
    isError: gameSelectionData.isError,
  };
};

export const useUpdateGameSelection = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      authToken: string;
      game_id: string;
      rank: { id: string; value: string };
    }) => {
      try {
        const res = await postUpdateGameSelection(
          args.authToken,
          args.game_id,
          args.rank
        );

        return res;
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: async () => queryClient.resetQueries(["gameSelections"]),
    }
  );

  return {
    mutateAsync: mutation.mutateAsync,
    mutateLoading: mutation.isLoading,
  };
};

export const useDeleteGameSelection = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      authToken: string;
      id: string;
      field_name: string;
      item_id: string;
    }) => {
      try {
        const res = await postDeleteGameSelection(
          args.authToken,
          args.id,
          args.field_name,
          args.item_id
        );

        return res;
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: async () => queryClient.resetQueries(["gameSelections"]),
    }
  );

  return {
    deleteAsync: mutation.mutateAsync,
    deleteLoading: mutation.isLoading,
  };
};

export const useShuffleGameSelection = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      authToken: string;
      id: string;
      field_name: string;
      shuffled_index: Array<number>;
    }) => {
      try {
        const res = await postShuffleGameSelection(
          args.authToken,
          args.id,
          args.field_name,
          args.shuffled_index
        );

        return res;
      } catch (e) {
        return null;
      }
    },
    {
      onSuccess: async () => queryClient.resetQueries(["gameSelections"]),
    }
  );

  return {
    shuffleAsync: mutation.mutateAsync,
    shuffleLoading: mutation.isLoading,
  };
};
